if (!customElements.get("product-form")) {
  customElements.define(
    "product-form",
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector("form");
        this.variantIdInput.disabled = false;
        this.form.addEventListener("submit", this.onSubmitHandler.bind(this));
        this.cart = document.querySelector("cart-notification") || document.querySelector("cart-drawer");
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector("span");

        if (document.querySelector("cart-drawer")) this.submitButton.setAttribute("aria-haspopup", "dialog");

        this.hideErrors = this.dataset.hideErrors === "true";
      }

      async onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute("aria-disabled") === "true") return;

        this.handleErrorMessage();

        this.submitButton.setAttribute("aria-disabled", true);
        this.submitButton.classList.add("loading");

        const spinner = this.querySelector(".loading__spinner");
        spinner?.classList.remove("hidden");

        try {
          const formData = new FormData(this.form);
          const mainVariantId = formData.get("id");

          if (!mainVariantId) throw new Error("Product variant ID is missing.");

          const extraItems = await this.getExtraItems(Number(mainVariantId));

          const config = fetchConfig("javascript");
          config.headers["X-Requested-With"] = "XMLHttpRequest";

          if (extraItems.length === 0) {
            delete config.headers["Content-Type"];

            if (this.cart) {
              formData.append(
                "sections",
                this.cart.getSectionsToRender().map((section) => section.id),
              );
              formData.append("sections_url", window.location.pathname);
              this.cart.setActiveElement(document.activeElement);
            }

            config.body = formData;
          } else {
            const payload = { items: [this.buildMainItem(formData, mainVariantId), ...extraItems] };

            if (this.cart) {
              payload.sections = this.cart.getSectionsToRender().map((section) => section.id);
              payload.sections_url = window.location.pathname;
              this.cart.setActiveElement(document.activeElement);
            }

            config.headers["Content-Type"] = "application/json";
            config.body = JSON.stringify(payload);
          }

          const fetchResponse = await fetch(`${routes.cart_add_url}`, config);
          const response = await fetchResponse.json();

          if (!fetchResponse.ok || response.status) {
            const errorMessage =
              response.description ||
              response.message ||
              window.cartStrings?.error ||
              "Something went wrong. Please try again.";

            publish(PUB_SUB_EVENTS.cartError, {
              source: "product-form",
              productVariantId: mainVariantId,
              errors: response.errors || response.description,
              message: response.message,
            });

            this.handleErrorMessage(errorMessage);

            const soldOutMessage = this.submitButton.querySelector(".sold-out-message");
            if (soldOutMessage) {
              this.submitButton.setAttribute("aria-disabled", true);
              this.submitButtonText.classList.add("hidden");
              soldOutMessage.classList.remove("hidden");
            }

            this.error = true;
            return;
          }

          if (!this.cart) {
            window.location = window.routes.cart_url;
            return;
          }

          this.error = false;
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: "product-form",
            productVariantId: mainVariantId,
            cartData: response,
          });

          // A multi-item add responds with { items: [...] } instead of the added line
          // item itself, so the cart drawer/notification lose the key and id they render from.
          const renderState = response.items ? { ...(response.items[0] || {}), sections: response.sections } : response;

          const quickAddModal = this.closest("quick-add-modal");
          if (quickAddModal) {
            document.body.addEventListener(
              "modalClosed",
              () => {
                setTimeout(() => {
                  this.cart.renderContents(renderState);
                });
              },
              { once: true },
            );
            quickAddModal.hide(true);
          } else {
            this.cart.renderContents(renderState);
          }
        } catch (error) {
          console.error("Product form error:", error);

          this.error = true;
          this.handleErrorMessage(window.cartStrings?.error || "Something went wrong. Please try again.");
        } finally {
          this.submitButton.classList.remove("loading");
          if (this.cart && this.cart.classList.contains("is-empty")) this.cart.classList.remove("is-empty");
          if (!this.error) this.submitButton.removeAttribute("aria-disabled");

          spinner?.classList.add("hidden");
        }
      }

      buildMainItem(formData, mainVariantId) {
        const item = {
          id: Number(mainVariantId),
          quantity: parseInt(formData.get("quantity") || "1", 10) || 1,
        };

        const properties = {};
        formData.forEach((value, key) => {
          const match = key.match(/^properties\[(.*)\]$/);
          if (!match || !match[1] || value === "" || value instanceof File) return;

          properties[match[1]] = value;
        });

        if (Object.keys(properties).length > 0) item.properties = properties;

        const sellingPlan = formData.get("selling_plan");
        if (sellingPlan) item.selling_plan = Number(sellingPlan);

        return item;
      }

      async getExtraItems(mainVariantId) {
        const scope = this.addonsScope;
        const items = [];

        const addonVariantIds = Array.from(scope.querySelectorAll("product-addons [data-addon-checkbox]:checked"))
          .map((checkbox) => parseInt(checkbox.value || "", 10))
          .filter((variantId) => variantId && variantId !== mainVariantId);

        addonVariantIds.forEach((variantId) => items.push({ id: variantId, quantity: 1 }));

        const giftVariantIds = [
          ...new Set(
            Array.from(scope.querySelectorAll("product-addons[data-gift-variant-id]"))
              .map((container) => parseInt(container.dataset.giftVariantId || "", 10))
              .filter((variantId) => variantId && variantId !== mainVariantId),
          ),
        ];

        if (giftVariantIds.length === 0) return items;

        // The gift is free and capped at one per cart, so a second add-to-cart must not
        // bump its quantity the way Shopify does for a repeated line item.
        const variantIdsInCart = await this.getVariantIdsInCart();

        giftVariantIds
          .filter((variantId) => !variantIdsInCart.has(variantId))
          .forEach((variantId) => items.push({ id: variantId, quantity: 1 }));

        return items;
      }

      async getVariantIdsInCart() {
        try {
          const response = await fetch(`${routes.cart_url}.js`, {
            headers: { "X-Requested-With": "XMLHttpRequest" },
          });
          if (!response.ok) return new Set();

          const cart = await response.json();
          return new Set((cart.items || []).map((item) => item.variant_id));
        } catch (error) {
          console.error("Product form: could not read the cart.", error);
          return new Set();
        }
      }

      get addonsScope() {
        const sectionId = this.dataset.sectionId;
        return (
          (sectionId && document.getElementById(`shopify-section-${sectionId}`)) ||
          this.closest(".shopify-section") ||
          document
        );
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector(".product-form__error-message-wrapper");
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector(".product-form__error-message");

        this.errorMessageWrapper.toggleAttribute("hidden", !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute("disabled", "disabled");
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute("disabled");
          this.submitButton.removeAttribute("aria-disabled");
          this.submitButtonText.textContent = window.variantStrings.addToCart;
          this.submitButtonText.classList.remove("hidden");
          const soldOutMessage = this.submitButton.querySelector(".sold-out-message");
          if (soldOutMessage) soldOutMessage.classList.add("hidden");
          this.error = false;
        }
      }

      get variantIdInput() {
        return this.form.querySelector("[name=id]");
      }
    },
  );
}
